import React, { useEffect, useState, useCallback, useRef } from 'react';

const REST = 'https://rest.amitis.network';
const RPC  = 'https://rpc.amitis.network';

export default function Explorer() {
  const [blocks, setBlocks]   = useState([]);
  const [txs, setTxs]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('blocks');
  const [search, setSearch]   = useState('');
  const [detail, setDetail]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stats, setStats]     = useState({});
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [statusRes, txRes] = await Promise.all([
        fetch(RPC + '/status').then(r => r.json()),
        fetch(REST + '/cosmos/tx/v1beta1/txs?order_by=2&pagination.limit=20').then(r => r.json()),
      ]);
      const latest = parseInt(statusRes.result?.sync_info?.latest_block_height || 0);
      const blockTime = statusRes.result?.sync_info?.latest_block_time;
      setStats({ height: latest, blockTime, totalTxs: txRes.pagination?.total || 0 });

      if (latest) {
        const heights = Array.from({length: 15}, (_,i) => latest - i).filter(h => h > 0);
        const bdata = await Promise.all(heights.map(h =>
          fetch(RPC + '/block?height=' + h).then(r => r.json()).catch(() => null)
        ));
        setBlocks(bdata.filter(Boolean).map(d => ({
          height: parseInt(d.result?.block?.header?.height || 0),
          hash: d.result?.block_id?.hash || '',
          time: d.result?.block?.header?.time || '',
          txCount: d.result?.block?.data?.txs?.length || 0,
          proposer: d.result?.block?.header?.proposer_address || '',
        })).filter(b => b.height > 0));
      }
      setTxs(txRes.tx_responses || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 12000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  function timeAgo(iso) {
    if (!iso) return '—';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return Math.floor(diff) + 's';
    if (diff < 3600) return Math.floor(diff/60) + 'm';
    return Math.floor(diff/3600) + 'h';
  }

  function shortHash(h, n=10) {
    if (!h) return '—';
    return h.slice(0,n) + '…' + h.slice(-6);
  }

  function txType(tx) {
    const t = tx.tx?.body?.messages?.[0]?.['@type'] || '';
    const name = t.split('.').pop()?.replace('Msg','') || 'Tx';
    const map = { Send:'Transfer', ExecuteContract:'Contract', StoreCode:'Deploy', InstantiateContract:'Init', Vote:'Gov Vote', Transfer:'IBC Transfer' };
    return map[name] || name;
  }

  async function openTx(hash) {
    setDetailLoading(true);
    setDetail({ type: 'tx', loading: true, hash });
    try {
      const res = await fetch(REST + '/cosmos/tx/v1beta1/txs/' + hash).then(r => r.json());
      setDetail({ type: 'tx', data: res.tx_response, hash });
    } catch { setDetail({ type: 'tx', error: true, hash }); }
    setDetailLoading(false);
  }

  async function openAddr(addr) {
    setDetail({ type: 'addr', loading: true, addr });
    try {
      const res = await fetch(REST + '/cosmos/bank/v1beta1/balances/' + addr).then(r => r.json());
      setDetail({ type: 'addr', data: res, addr });
    } catch { setDetail({ type: 'addr', error: true, addr }); }
  }

  async function doSearch() {
    const q = search.trim();
    if (!q) return;
    if (/^\d+$/.test(q)) {
      const res = await fetch(RPC + '/block?height=' + q).then(r => r.json()).catch(() => null);
      if (res?.result?.block) { setDetail({ type: 'block', data: res.result, height: q }); return; }
    }
    if (/^[0-9A-Fa-f]{64}$/.test(q)) { openTx(q.toUpperCase()); return; }
    if (q.startsWith('amitis1')) { openAddr(q); return; }
    setDetail({ type: 'notfound', q });
  }

  const mono = { fontFamily: 'DM Mono, monospace' };

  return (
    <div className="page-content fade-up">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <span className="section-label">On-Chain Data</span>
          <div style={{ fontSize: 22, fontWeight: 800 }}>Explorer</div>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: 'Block', val: stats.height ? '#' + stats.height.toLocaleString() : '—', color: 'var(--green)' },
            { label: 'Txns', val: stats.totalTxs ? parseInt(stats.totalTxs).toLocaleString() : '—', color: 'var(--white)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: '10px 18px', textAlign: 'center', minWidth: 100 }}>
              <div style={{ ...mono, fontSize: 8, color: 'var(--dimmer)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
              <div style={{ ...mono, fontSize: 16, fontWeight: 700, color: s.color }}>{s.val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <input
          className="input-field"
          placeholder="Block height, tx hash, or address…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          style={{ flex: 1, ...mono, fontSize: 12 }}
        />
        <button className="btn btn-primary btn-sm" onClick={doSearch}>Search</button>
        <button className="btn btn-outline btn-sm" onClick={load}>↺</button>
      </div>

      {/* Detail pane */}
      {detail && (
        <div className="card fade-up" style={{ marginBottom: 16, borderColor: 'var(--green-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ ...mono, fontSize: 10, color: 'var(--green)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {detail.type === 'tx' ? 'Transaction' : detail.type === 'addr' ? 'Address' : detail.type === 'block' ? 'Block' : 'Search Result'}
            </div>
            <button className="btn btn-outline btn-sm" style={{ padding: '2px 10px', fontSize: 11 }} onClick={() => setDetail(null)}>✕</button>
          </div>
          {detail.loading && <div className="loading-row"><div className="spinner"></div>Loading…</div>}
          {detail.error && <div style={{ color: 'var(--red)', ...mono, fontSize: 12 }}>Not found</div>}
          {detail.type === 'notfound' && <div style={{ color: 'var(--muted)', ...mono, fontSize: 12 }}>No results for "{detail.q}"</div>}
          {detail.type === 'tx' && detail.data && (() => {
            const tx = detail.data;
            const msgs = tx.tx?.body?.messages || [];
            return (
              <div>
                {[
                  ['Hash', tx.txhash],
                  ['Status', tx.code === 0 ? '✓ Success' : `✗ Error (${tx.code})`],
                  ['Block', '#' + parseInt(tx.height).toLocaleString()],
                  ['Time', new Date(tx.timestamp).toLocaleString()],
                  ['Type', txType(tx)],
                  msgs[0]?.from_address ? ['From', msgs[0].from_address] : null,
                  msgs[0]?.to_address ? ['To', msgs[0].to_address] : null,
                  ['Gas', `${parseInt(tx.gas_used).toLocaleString()} / ${parseInt(tx.gas_wanted).toLocaleString()}`],
                  tx.tx?.body?.memo ? ['Memo', tx.tx.body.memo] : null,
                ].filter(Boolean).map(([k, v]) => (
                  <div key={k} className="data-row" style={{ padding: '6px 0' }}>
                    <span className="data-label" style={{ ...mono, fontSize: 11 }}>{k}</span>
                    <span className={`data-value ${k === 'Status' && tx.code === 0 ? 'green' : ''}`} style={{ ...mono, fontSize: 11, wordBreak: 'break-all' }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })()}
          {detail.type === 'addr' && detail.data && (
            <div>
              <div className="data-row" style={{ padding: '6px 0' }}>
                <span className="data-label" style={{ ...mono, fontSize: 11 }}>Address</span>
                <span className="data-value" style={{ ...mono, fontSize: 11, wordBreak: 'break-all' }}>{detail.addr}</span>
              </div>
              {(detail.data.balances || []).map((b, i) => (
                <div key={i} className="data-row" style={{ padding: '6px 0' }}>
                  <span className="data-label" style={{ ...mono, fontSize: 11 }}>{b.denom === 'uamts' ? 'AMTS Balance' : b.denom.slice(0,20)}</span>
                  <span className="data-value green" style={{ ...mono, fontSize: 11 }}>
                    {b.denom === 'uamts' ? (parseInt(b.amount)/1e6).toLocaleString(undefined,{maximumFractionDigits:6}) + ' AMTS' : parseInt(b.amount).toLocaleString() + ' ' + b.denom}
                  </span>
                </div>
              ))}
              {!(detail.data.balances?.length) && <div style={{ color: 'var(--muted)', ...mono, fontSize: 12 }}>No balances</div>}
            </div>
          )}
          {detail.type === 'block' && detail.data && (() => {
            const b = detail.data;
            return (
              <div>
                {[
                  ['Height', '#' + parseInt(b.block?.header?.height).toLocaleString()],
                  ['Hash', b.block_id?.hash],
                  ['Time', new Date(b.block?.header?.time).toLocaleString()],
                  ['Transactions', b.block?.data?.txs?.length || 0],
                  ['Proposer', b.block?.header?.proposer_address],
                ].map(([k,v]) => (
                  <div key={k} className="data-row" style={{ padding: '6px 0' }}>
                    <span className="data-label" style={{ ...mono, fontSize: 11 }}>{k}</span>
                    <span className="data-value" style={{ ...mono, fontSize: 11, wordBreak: 'break-all' }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['blocks','txs'].map(t => (
          <button key={t} className={`btn btn-sm ${view === t ? 'btn-primary' : 'btn-outline'}`} onClick={() => setView(t)}>
            {t === 'blocks' ? 'Blocks' : 'Transactions'}
          </button>
        ))}
      </div>

      {/* Data table */}
      <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
        {loading ? (
          <div className="loading-row"><div className="spinner"></div>Loading chain data…</div>
        ) : view === 'blocks' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                {['Height','Hash','Txns','Proposer','Age'].map(h => (
                  <th key={h} style={{ ...mono, fontSize: 9, color: 'var(--dimmer)', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 400, padding: '10px 16px', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blocks.map(b => (
                <tr key={b.height} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.15s' }}
                  onClick={() => setDetail({ type: 'block', data: { block: { header: { height: b.height, time: b.time, proposer_address: b.proposer }, data: { txs: Array(b.txCount) } }, block_id: { hash: b.hash } } })}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}
                >
                  <td style={{ ...mono, fontSize: 12, fontWeight: 700, color: 'var(--green)', padding: '11px 16px' }}>#{b.height.toLocaleString()}</td>
                  <td style={{ ...mono, fontSize: 11, color: 'var(--blue)', padding: '11px 16px' }}>{shortHash(b.hash)}</td>
                  <td style={{ ...mono, fontSize: 11, padding: '11px 16px' }}>{b.txCount}</td>
                  <td style={{ ...mono, fontSize: 10, color: 'var(--purple)', padding: '11px 16px' }}>{shortHash(b.proposer, 8)}</td>
                  <td style={{ ...mono, fontSize: 10, color: 'var(--muted)', padding: '11px 16px' }}>{timeAgo(b.time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                {['','Hash','Type','Block','Age'].map((h,i) => (
                  <th key={i} style={{ ...mono, fontSize: 9, color: 'var(--dimmer)', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 400, padding: '10px 16px', textAlign: 'left', width: i===0?'30px':undefined }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txs.map((tx, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.15s' }}
                  onClick={() => openTx(tx.txhash)}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}
                >
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: tx.code===0?'rgba(74,222,128,0.15)':'rgba(239,68,68,0.15)', border: `1px solid ${tx.code===0?'rgba(74,222,128,0.3)':'rgba(239,68,68,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: tx.code===0?'var(--green)':'var(--red)' }}>
                      {tx.code===0?'✓':'✗'}
                    </div>
                  </td>
                  <td style={{ ...mono, fontSize: 11, color: 'var(--blue)', padding: '11px 16px' }}>{shortHash(tx.txhash)}</td>
                  <td style={{ ...mono, fontSize: 10, color: 'var(--muted)', padding: '11px 16px' }}>{txType(tx)}</td>
                  <td style={{ ...mono, fontSize: 11, color: 'var(--green)', padding: '11px 16px' }}>#{parseInt(tx.height).toLocaleString()}</td>
                  <td style={{ ...mono, fontSize: 10, color: 'var(--muted)', padding: '11px 16px' }}>{timeAgo(tx.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
